import type { SCREENS } from '../../../api/src/features/events/kinds.ts'
import { apiUrl } from '../api/client.ts'
import type { DeviceClass, WidthBucket } from './device.ts'
import { createEventSink } from './eventSink.ts'
import { visitId } from './visitId.ts'

/**
 * What a teacher did in the page that the server never sees: copying a link, opening a print
 * dialog, watching a tool's intro, starting the next tool from a source, searching her desk.
 * Everything the server does itself (drafting, editing, publishing) it records itself, so none of
 * that is here. `apps/api/src/features/events/kinds.ts` is the authority on the shapes; a test
 * checks these against it.
 *
 * Refs say which of her things an event is about; the server keeps only the ones that are hers.
 */

/** Every screen a teacher can be on: the api's closed list, so a new one is added in one place. */
export type Screen = (typeof SCREENS)[number]

/** The "keep your work" asks a guest is shown: the layout banner, its hand-in line, the worksheet page, the share sheet. */
export type PromptShown =
	| 'keep_work'
	| 'keep_work_print'
	| 'keep_work_sheet'
	| 'keep_work_handed_in'

export type WaitWhat =
	| 'draft'
	| 'photo'
	| 'video_import'
	| 'revise'
	| 'regenerate'
	| 'more'
	| 'plan'
	| 'words'
	| 'levels'
	| 'follow_up'

/** The steps of "read this text aloud" the page reports (the api's `audio_step`). */
export type AudioStep = 'shown' | 'needs_account' | 'asked' | 'blocked' | 'cancelled' | 'played'

export type TeacherEvent =
	| { kind: 'link_copied'; data: { what: 'activity' | 'word_list' } }
	| {
			kind: 'print_opened'
			data: { what: 'worksheet' | 'levels' | 'plan' | 'word_list' | 'puzzles' | 'groups' }
	  }
	| {
			kind: 'tool_intro'
			data: { tool: string; outcome: 'watched' | 'skipped' | 'closed'; seconds: number }
	  }
	| { kind: 'make_from_this'; data: { tool: 'exercises' | 'words' | 'plans' | 'levels' | 'wheel' } }
	| { kind: 'audio_step'; data: { step: AudioStep } }
	| { kind: 'search_used'; data: { results: number } }
	| {
			kind: 'visit_started'
			data: {
				device: DeviceClass
				width: WidthBucket
				lite: boolean
				automated: boolean
				build: string
				bootMs: number | null
				entry: Screen | 'other'
				uiLang: string
				returning: boolean
			}
	  }
	| { kind: 'preview_opened'; data: Record<string, never> }
	| { kind: 'publish_blocked_pressed'; data: { faults: number } }
	| { kind: 'publish_without'; data: { count: number } }
	| { kind: 'publish_without_undone'; data: { count: number } }
	| { kind: 'continue_clicked'; data: { state: 'draft' | 'published' } }
	| { kind: 'example_used'; data: Record<string, never> }
	| { kind: 'screen_viewed'; data: { screen: Screen; items?: number } }
	| {
			kind: 'screen_left'
			data: {
				screen: Screen
				ms: number
				how: 'navigated' | 'hidden'
				scroll: 0 | 25 | 50 | 75 | 100
				clicks: number
				rage: number
				clicked: string[]
				seen: number
				items: number
			}
	  }
	| {
			kind: 'start_left'
			data: {
				typed: 'none' | 'few' | 'some' | 'lots'
				link: boolean
				files: number
				pressed: number
				ms: number
			}
	  }
	| { kind: 'refused'; data: { where: string; why: string } }
	| {
			kind: 'wait'
			data: {
				what: WaitWhat
				ms: number
				outcome: 'done' | 'failed' | 'left'
				status?: number
				exerciseId?: string
			}
	  }
	| {
			kind: 'share_closed'
			data: {
				via: 'publish' | 'share_button'
				ms: number
				copied: 'none' | 'button' | 'manual'
				next: 'none' | 'another' | 'results' | 'preview'
				changed: number
			}
	  }
	| { kind: 'prompt_shown'; data: { which: PromptShown } }
	| {
			kind: 'draft_rated'
			data: {
				value: 'good' | 'ok' | 'poor'
				why?: 'level' | 'language' | 'mistakes' | 'too_easy' | 'too_hard' | 'too_few'
			}
	  }
	| {
			kind: 'vitals'
			data: {
				screen: Screen | 'other'
				lcp: number | null
				inp: number | null
				cls: number | null
				shifted?: string
			}
	  }

export type Refs = {
	activityId?: string
	wordListId?: string
	planId?: string
	artifactId?: string
}

const sink = createEventSink(() => apiUrl().events.$url().toString())
// The teacher's sink lives as long as the page, so it never stops listening.
sink.listen()

export function track(event: TeacherEvent, refs?: Refs): void {
	const clean = refs && Object.fromEntries(Object.entries(refs).filter(([, v]) => v))
	sink.push({
		...event,
		visit: visitId(),
		...(clean && Object.keys(clean).length > 0 && { refs: clean as Record<string, string> }),
	})
}

/**
 * For the moment the page is going away: sent now through the beacon, not with the next batch. The
 * sink flushes on hide too, but the listener that decides to report can run after it, and an event
 * pushed after that flush would never leave.
 */
export function trackNow(event: TeacherEvent, refs?: Refs): void {
	track(event, refs)
	sink.flush(true)
}

/** Sends what is queued and waits for it: before signing out, while the session can still record it. */
export const flushEvents = (): Promise<void> => sink.flush()

/** The page's own print dialog, counted first; the dialog is the browser's and cannot be observed. */
export function printPage(
	what: Extract<TeacherEvent, { kind: 'print_opened' }>['data']['what'],
	refs?: Refs,
): void {
	track({ kind: 'print_opened', data: { what } }, refs)
	window.print()
}

let searchTimer: ReturnType<typeof setTimeout> | undefined
/**
 * A search she made, once she stops typing: how many things matched, never what she typed.
 * `count` is read when the pause ends, so it is the result she was left looking at.
 */
export function noteSearch(query: string, count: () => number): void {
	clearTimeout(searchTimer)
	if (query.trim().length < 2) return
	searchTimer = setTimeout(() => track({ kind: 'search_used', data: { results: count() } }), 1500)
}

/** More of the same refusal on one page is the same story: five is enough to show she kept trying. */
const MAX_REFUSALS = 5
const refusals = new Map<string, number>()
/**
 * A refusal the server never saw: a file that is too big, a form left incomplete, a button that
 * would not act. `where` and `why` are short words from the call site, never her text; the api
 * drops anything else. `refs` say which of her things it was about, when the page knows. Called
 * beside the message she is shown.
 */
export function refused(where: string, why: string, refs?: Refs): void {
	const key = `${where}/${why}`
	const n = refusals.get(key) ?? 0
	if (n >= MAX_REFUSALS) return
	refusals.set(key, n + 1)
	track({ kind: 'refused', data: { where, why } }, refs)
}
